if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // Remember the normal button text
        this.originalButtonText = this.submitButtonText
          ? this.submitButtonText.textContent
          : 'Book Now';

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Create our customer-friendly error box
        this.createBookingMessageBox();
      }

      createBookingMessageBox() {
        if (this.querySelector('.booking-feedback-message')) return;

        const messageBox = document.createElement('div');
        messageBox.className = 'booking-feedback-message';
        messageBox.setAttribute('role', 'alert');
        messageBox.setAttribute('aria-live', 'assertive');
        messageBox.hidden = true;

        messageBox.style.marginTop = '12px';
        messageBox.style.padding = '12px 14px';
        messageBox.style.borderRadius = '6px';
        messageBox.style.fontSize = '14px';
        messageBox.style.lineHeight = '1.4';

        this.submitButton.insertAdjacentElement('afterend', messageBox);

        this.bookingMessageBox = messageBox;
      }

      showBookingMessage(message, type = 'error') {
        this.bookingMessageBox =
          this.bookingMessageBox ||
          this.querySelector('.booking-feedback-message');

        if (!this.bookingMessageBox) return;

        this.bookingMessageBox.hidden = false;
        this.bookingMessageBox.textContent = message;

        if (type === 'success') {
          this.bookingMessageBox.style.background = '#edf7ed';
          this.bookingMessageBox.style.border = '1px solid #4caf50';
          this.bookingMessageBox.style.color = '#1e4620';
        } else {
          this.bookingMessageBox.style.background = '#fff1f0';
          this.bookingMessageBox.style.border = '1px solid #d93025';
          this.bookingMessageBox.style.color = '#8a1c13';
        }
      }

      hideBookingMessage() {
        this.bookingMessageBox =
          this.bookingMessageBox ||
          this.querySelector('.booking-feedback-message');

        if (this.bookingMessageBox) {
          this.bookingMessageBox.hidden = true;
          this.bookingMessageBox.textContent = '';
        }
      }

      setLoadingState() {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');

        if (this.submitButtonText) {
          this.submitButtonText.textContent = 'Adding booking...';
        }

        const spinner = this.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }
      }

      resetLoadingState() {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');

        if (this.submitButtonText) {
          this.submitButtonText.textContent = this.originalButtonText;
        }

        const spinner = this.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.add('hidden');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.handleErrorMessage();
        this.hideBookingMessage();
        this.setLoadingState();

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // IMPORTANT:
        // This keeps ALL BookThatApp fields inside the form.
        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );

          formData.append('sections_url', window.location.pathname);

          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            /*
             * Shopify / app returned an error
             */
            if (response.status) {
              const errorMessage =
                response.description ||
                response.message ||
                response.errors ||
                'There was a problem adding your booking. Please check your booking details and try again.';

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(errorMessage);

              this.showBookingMessage(
                typeof errorMessage === 'string'
                  ? errorMessage
                  : 'Please check your booking details and try again.',
                'error'
              );

              this.error = true;

              this.resetLoadingState();

              return;
            }

            /*
             * SUCCESS
             */
            this.error = false;

            if (this.submitButtonText) {
              this.submitButtonText.textContent = 'Booking added ✓';
            }

            const spinner = this.querySelector('.loading__spinner');

            if (spinner) {
              spinner.classList.add('hidden');
            }

            this.showBookingMessage(
              'Booking added successfully. Taking you to your cart...',
              'success'
            );

            /*
             * If the theme doesn't use a cart drawer,
             * go straight to the cart.
             */
            if (!this.cart) {
              setTimeout(() => {
                window.location = window.routes.cart_url;
              }, 500);

              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }

            /*
             * Restore button after cart opens
             */
            setTimeout(() => {
              this.resetLoadingState();
              this.hideBookingMessage();
            }, 1500);
          })

          .catch((error) => {
            console.error('Booking error:', error);

            this.error = true;

            this.showBookingMessage(
              'Something went wrong while adding your booking. Please check your booking details and try again.',
              'error'
            );

            this.resetLoadingState();
          })

          .finally(() => {
            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart ||
              this.originalButtonText;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}